/**
 * SetPasswordForm - Set new password for first-time users
 */
import React, { useState } from 'react';
import { useAuthContext } from '../../context/AuthContext';
import { useAuth } from '../../hooks/useAuth';
import { useAuthForm } from '../../hooks/useAuthForm';
import { AuthHeader, AuthInput, AuthButton, PasswordRequirements } from '../ui';

function SetPasswordForm() {
  const { loading, goBack } = useAuthContext();
  const { setNewPassword } = useAuth();
  const { 
    password, 
    confirmPassword, 
    error, 
    handlePasswordChange,
    handleConfirmPasswordChange,
    getPasswordValidation,
    doPasswordsMatch,
  } = useAuthForm();
  
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const passwordValidation = getPasswordValidation();
  const isFormValid = passwordValidation.isValid && doPasswordsMatch();
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isFormValid) return;
    
    await setNewPassword();
  };
  
  const EyeIcon = (show) => show ? (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
    </svg>
  ) : (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
  );
  
  return (
    <div className="relative p-6 md:p-8 bg-white">
      <AuthHeader
        title="Set Your Password"
        subtitle="Create a secure password for your account"
        showBack
        onBack={goBack}
      />
      
      <form onSubmit={handleSubmit} className="space-y-5">
        <PasswordRequirements validation={passwordValidation} />
        
        <AuthInput
          label="New Password"
          type={showPassword ? 'text' : 'password'}
          value={password}
          onChange={handlePasswordChange}
          placeholder="Enter new password"
          disabled={loading}
          rightIcon={EyeIcon(showPassword)}
          onRightIconClick={() => setShowPassword(!showPassword)}
        />
        
        <AuthInput
          label="Confirm Password"
          type={showConfirmPassword ? 'text' : 'password'}
          value={confirmPassword}
          onChange={handleConfirmPasswordChange}
          placeholder="Re-enter password"
          error={confirmPassword && !doPasswordsMatch() ? 'Passwords do not match' : ''}
          disabled={loading}
          rightIcon={EyeIcon(showConfirmPassword)}
          onRightIconClick={() => setShowConfirmPassword(!showConfirmPassword)}
        />
        
        {error && (
          <p className="text-sm text-red-500 text-center">{error}</p>
        )}
        
        <AuthButton
          type="submit"
          loading={loading}
          disabled={!isFormValid}
        >
          Set Password
        </AuthButton>
      </form>
    </div>
  );
}

export default SetPasswordForm;
